/**
 * PropertyVideoWizard Sub-components
 *
 * Reusable components for the property video generation wizard:
 * - WizardStepIndicatorDynamic - Progress stepper with dynamic labels
 * - ScrapingLoadingState - Loading states during scraping
 * - ErrorDisplay - Error message with retry option
 */

import { Loader2, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type {
  IWizardStepIndicatorDynamicProps,
  IScrapingLoadingStateProps,
  IErrorDisplayProps,
} from '@/@types/property';
import { NEW_PROPERTY_STEP_LABELS, DRAFT_PROPERTY_STEP_LABELS } from '@/@types/property';

export const DEFAULT_VOICE_ID = '21m00Tcm4TlvDq8ikWAM'; // Rachel
export const DEFAULT_DURATION = 30;

export { NEW_PROPERTY_STEP_LABELS, DRAFT_PROPERTY_STEP_LABELS };

// ============================================================================
// WizardStepIndicatorDynamic
// ============================================================================

/**
 * Displays the wizard progress with dynamic step labels
 * Supports both new property flow (6 steps) and draft property flow (4 steps)
 * Completed steps are clickable for navigation
 */
function WizardStepIndicatorDynamic({
  currentStep,
  totalSteps,
  stepLabels,
  onStepClick,
  finalStepClickable,
}: IWizardStepIndicatorDynamicProps) {
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between">
        {stepLabels.map((label, index) => {
          const stepNumber = index + 1;
          const isActive = stepNumber === currentStep;
          const isCompleted = stepNumber < currentStep;
          const isFinalStepWithVideo = stepNumber === totalSteps && finalStepClickable;
          const isClickable = (isCompleted || isFinalStepWithVideo) && onStepClick;

          const baseClasses = `flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-all ${
            isActive
              ? 'bg-primary text-primary-foreground'
              : isCompleted || isFinalStepWithVideo
                ? 'bg-primary/20 text-primary cursor-pointer hover:scale-105 hover:ring-2 hover:ring-primary/50'
                : 'bg-muted text-muted-foreground'
          }`;

          return (
            <div key={label} className="flex flex-1 items-center">
              <div className="flex flex-col items-center">
                {isClickable ? (
                  <button
                    type="button"
                    onClick={() => onStepClick(stepNumber)}
                    aria-label={`Go to step ${stepNumber}: ${label}`}
                    title={`Go to: ${label}`}
                    className={`${baseClasses} focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2`}
                  >
                    {stepNumber}
                  </button>
                ) : (
                  <span className={baseClasses}>{stepNumber}</span>
                )}
                <span
                  className={`mt-1 text-xs ${
                    isActive ? 'font-medium text-foreground' : 'text-muted-foreground'
                  }`}
                >
                  {label}
                </span>
              </div>
              {index < totalSteps - 1 && (
                <div
                  className={`mx-2 h-0.5 flex-1 ${isCompleted ? 'bg-primary/40' : 'bg-muted'}`}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export { WizardStepIndicatorDynamic };

// ============================================================================
// ScrapingLoadingState
// ============================================================================

/**
 * Loading state display during scraping/processing
 */
function ScrapingLoadingState({ status }: IScrapingLoadingStateProps) {
  const statusLabels: Record<string, string> = {
    scraping: 'Scraping property data...',
    analyzing_location: 'Analyzing nearby locations...',
    generating_description: 'Generating property description...',
  };

  const label = statusLabels[status] ?? 'Processing...';

  return (
    <Card className="w-full max-w-2xl">
      <CardContent className="flex flex-col items-center justify-center py-16">
        <Loader2 className="mb-4 h-12 w-12 animate-spin text-primary" aria-hidden="true" />
        <h3 className="mb-2 text-lg font-medium">{label}</h3>
        <p className="text-sm text-muted-foreground">This may take a moment. Please wait...</p>
      </CardContent>
    </Card>
  );
}

export { ScrapingLoadingState };

// ============================================================================
// ErrorDisplay
// ============================================================================

/**
 * Error display with retry option
 */
function ErrorDisplay({ message, onRetry }: IErrorDisplayProps) {
  return (
    <Card className="w-full max-w-2xl border-destructive/50">
      <CardContent className="flex flex-col items-center justify-center py-12">
        <AlertTriangle className="mb-4 h-12 w-12 text-destructive" aria-hidden="true" />
        <h3 className="mb-2 text-lg font-medium text-destructive">Something went wrong</h3>
        <p className="mb-6 max-w-md text-center text-sm text-muted-foreground">{message}</p>
        <Button onClick={onRetry} variant="outline">
          Try Again
        </Button>
      </CardContent>
    </Card>
  );
}

export { ErrorDisplay };
